import type { PersonalInfo } from '@/api/generated/model';
import { authService, settingsService } from '@/api/services';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/i18n/LanguageContext';
import { cn } from '@/lib/utils';
import { ChevronDown, LogOut, Settings } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

interface NavItemProps {
  icon: React.ReactNode;
  label: string;
  href: string;
  active: boolean;
  onClick?: () => void;
}

const NavItem = ({ icon, label, href, active, onClick }: NavItemProps) => {
  return (
    <Link to={href} onClick={onClick}>
      <Button
        variant="ghost"
        className={cn(
          'mb-1 w-full justify-start gap-2',
          active && 'bg-secondary text-primary font-medium'
        )}
      >
        {icon}
        {label}
      </Button>
    </Link>
  );
};

interface UserProfileProps {
  isMobile?: boolean;
  onNavigation?: () => void;
}

export const UserProfile = ({ isMobile = false, onNavigation }: UserProfileProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isProfileExpanded, setIsProfileExpanded] = useState(false);
  const { t } = useLanguage();
  const [userInfo, setUserInfo] = useState<PersonalInfo | null>(null);
  // const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  // const [isLoading, setIsLoading] = useState(true);
  // const [currentVehicleId, setCurrentVehicleId] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const info = await settingsService.getPersonalInfo();
        setUserInfo(info);
      } catch (error) {
        console.error('Failed to fetch user info:', error);
      }
    };
    fetchUserInfo();
  }, []);

  // useEffect(() => {
  //   const fetchVehicles = async () => {
  //     try {
  //       setIsLoading(true);
  //       const fetchedVehicles = await vehicleService.getVehicles();
  //       setVehicles(fetchedVehicles);
  //
  //       // Get current vehicle ID from localStorage
  //       const storedCurrentVehicleId = vehicleService.getCurrentVehicleId();
  //       setCurrentVehicleId(storedCurrentVehicleId);
  //
  //       // If no current vehicle is set, set the first one as current
  //       if (!storedCurrentVehicleId && fetchedVehicles.length > 0) {
  //         vehicleService.setCurrentVehicleId(fetchedVehicles[0].id);
  //         setCurrentVehicleId(fetchedVehicles[0].id);
  //       }
  //     } catch (error) {
  //       console.error('Failed to fetch vehicles:', error);
  //     } finally {
  //       setIsLoading(false);
  //     }
  //   };
  //   fetchVehicles();
  // }, []);

  const handleLogout = () => {
    authService.removeToken();
    navigate('/login');
    if (onNavigation) {
      onNavigation();
    }
  };

  // const handleVehicleChange = async (vehicleId: string) => {
  //   try {
  //     // Update the current vehicle ID in localStorage
  //     vehicleService.setCurrentVehicleId(vehicleId);
  //     setCurrentVehicleId(vehicleId);
  //
  //     // Update the vehicle in the API
  //     const vehicle = vehicles.find((v) => v.id === vehicleId);
  //     if (!vehicle) return;
  //
  //     await vehicleService.updateVehicle(vehicleId, {
  //       ...vehicle,
  //     });
  //   } catch (error) {
  //     console.error('Failed to update current vehicle:', error);
  //   }
  // };

  // Helper function to check if a path is active
  const isPathActive = (path: string) => {
    // For the home route, only match exactly
    if (path === '/') {
      return location.pathname === path;
    }
    // For other routes, match if the path starts with the route path
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  return (
    <div className={cn('border-t pt-6', isMobile ? 'mt-6' : 'mt-auto')}>
      <div className="space-y-2">
        <div
          className="hover:bg-secondary/50 flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 transition-colors"
          onClick={() => setIsProfileExpanded(!isProfileExpanded)}
        >
          <Avatar className="h-8 w-8">
            <AvatarImage src="/avatars/01.png" alt={userInfo?.firstName || 'User'} />
            <AvatarFallback>{userInfo?.firstName?.[0] || 'U'}</AvatarFallback>
          </Avatar>
          <div className="flex flex-1 flex-col items-start">
            <div className="flex w-full items-center justify-between">
              <span className="text-sm font-medium">
                {userInfo ? `${userInfo.firstName} ${userInfo.lastName}` : t('common.loading')}
              </span>
              <ChevronDown
                size={16}
                className={cn(
                  'transition-transform duration-200',
                  isProfileExpanded && 'rotate-180 transform'
                )}
              />
            </div>
            <span className="text-muted-foreground text-xs">{userInfo?.email || ''}</span>
          </div>
        </div>
        <div
          className={cn(
            'overflow-hidden transition-all duration-200',
            isProfileExpanded ? 'max-h-[300px]' : 'max-h-0'
          )}
        >
          <div className="space-y-1">
            <div className="px-2 py-1">
              <div className="text-muted-foreground mb-1 px-2 text-xs font-medium">
                {t('nav.vehicles')}
              </div>
              {/*<div className="space-y-1">*/}
              {/*  {isLoading ? (*/}
              {/*    <div className="flex items-center justify-center py-2">*/}
              {/*      <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />*/}
              {/*      <span className="text-muted-foreground ml-2 text-xs">*/}
              {/*        {t('common.loading')}*/}
              {/*      </span>*/}
              {/*    </div>*/}
              {/*  ) : vehicles.length === 0 ? (*/}
              {/*    <div className="text-muted-foreground px-2 py-2 text-center text-xs">*/}
              {/*      {t('common.noResults')}*/}
              {/*    </div>*/}
              {/*  ) : (*/}
              {/*    vehicles.map((vehicle) => (*/}
              {/*      <Button*/}
              {/*        key={vehicle.id}*/}
              {/*        variant="ghost"*/}
              {/*        className={cn(*/}
              {/*          'w-full justify-between',*/}
              {/*          currentVehicleId === vehicle.id && 'bg-secondary text-primary font-medium'*/}
              {/*        )}*/}
              {/*        onClick={() => handleVehicleChange(vehicle.id)}*/}
              {/*      >*/}
              {/*        <span>{vehicle.name}</span>*/}
              {/*        {currentVehicleId === vehicle.id && <Check size={16} />}*/}
              {/*      </Button>*/}
              {/*    ))*/}
              {/*  )}*/}
              {/*</div>*/}
            </div>
            <NavItem
              icon={<Settings size={20} />}
              label={t('nav.settings')}
              href="/settings"
              active={isPathActive('/settings')}
              onClick={onNavigation}
            />
            <Button
              variant="ghost"
              className="text-destructive w-full justify-start gap-2"
              onClick={handleLogout}
            >
              <LogOut size={20} />
              {t('nav.logout')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
